import React, { useEffect, useState, useRef } from 'react';
import { CartApiService } from '../../../service/cartApiService';
import { ToastTypes, useMessage } from '../../../../base/context/MessageProvider';
import { MdClose, MdContentCopy, MdCheck, MdAccountBalance, MdQrCode } from 'react-icons/md';
import { FiUpload } from 'react-icons/fi';
import { getAuthLocalStorage } from '../../../../storage/localStorage';

export default function BankTransferModal({ isOpen, onClose, cartData, priceSummaryTotals, totalLMDWithGST, totalLMDCharges, gstOnLMD, shippingMethod, cart, grandTotal, onOrderPlaced, imPointsDiscount, imCashDiscount, paymentMethods, grossOrderTotal }) {
  const [settings, setSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [txnId, setTxnId] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const fileInputRef = useRef(null);
  const message = useMessage();
  // Use grandTotal if provided (Grand Total after IM discounts), otherwise fallback to old calculation
  const totalPayable = grandTotal !== undefined ? grandTotal : (priceSummaryTotals?.TOTAL_PRICE || 0) + (totalLMDWithGST || totalLMDCharges || 0);

  useEffect(() => {
    if (!isOpen) return;
    setLoadingSettings(true);
    CartApiService.bankTransferSettings()
      .then((res) => {
        if (res?.status?.code === 200) setSettings(res.data);
      })
      .catch(() => { })
      .finally(() => setLoadingSettings(false));

    // Lead is already recorded when the user arrives at the payment page (payment/index.js)
  }, [isOpen]);

  const handleCopy = (text, field) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      message.showToast('Only JPG, PNG, or PDF files are allowed.', ToastTypes.ERROR);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.showToast('File size must be under 5MB.', ToastTypes.ERROR);
      return;
    }
    setProofFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setProofPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setProofPreview(null);
    }
  };

  const uploadProofToServer = async () => {
    if (!proofFile) return '';
    setUploadingProof(true);
    try {
      const formData = new FormData();
      formData.append('image', proofFile);
      const loginHeader = getAuthLocalStorage()?.loginHeader;
      const res = await fetch(process.env.REACT_APP_APIUrl + 'recharge-proof', {
        method: 'POST',
        headers: { Authorization: `Bearer ${loginHeader}` },
        body: formData,
      });
      const data = await res.json();
      if (data?.success && data?.url) return data.url;
      return '';
    } catch {
      return '';
    } finally {
      setUploadingProof(false);
    }
  };

  const handlePlaceOrder = async () => {
    // Old: transaction ID or payment proof (either one)
    // if (!txnId.trim() && !proofFile) {
    //   message.showToast('Please enter the transaction ID or upload a payment proof.', ToastTypes.WARNING);
    //   return;
    // }
    // New: payment proof upload is mandatory
    if (!proofFile) {
      message.showToast('Please upload a payment proof to continue.', ToastTypes.WARNING);
      return;
    }
    if (placing) return;
    setPlacing(true);

    // Old: optional proof upload; txn ID could satisfy requirement
    // let paymentProofUrl = '';
    // if (proofFile) {
    //   paymentProofUrl = await uploadProofToServer();
    //   if (!paymentProofUrl && !txnId.trim()) {
    //     setPlacing(false);
    //     message.showToast(
    //       'Payment proof upload failed. Please re-upload proof or enter the transaction ID.',
    //       ToastTypes.ERROR
    //     );
    //     return;
    //   }
    // }
    // New: always upload proof before placing order
    const paymentProofUrl = await uploadProofToServer();
    if (!paymentProofUrl) {
      setPlacing(false);
      message.showToast('Payment proof upload failed. Please re-upload and try again.', ToastTypes.ERROR);
      return;
    }

    const payload = {
      buyDirect: cartData.buyDirect,
      productId: cartData.productId,
      quantity: cartData.quantity,
      shippingAddress: cartData.shippingAddress,
      billingAddress: cartData.billingAddress,
      useSameAddressForBilling: cartData.useSameAddressForBilling,
      shippingMethod: shippingMethod || cartData.shippingMethod || 'fulfillment',
      totalLMDCharges: totalLMDWithGST || totalLMDCharges || 0,
      gstOnLMD: gstOnLMD || 0,
      warehouse: cartData.warehouse || null,
      customerNote: cartData.customerNote || '',
      // bankTransferTxnId: txnId.trim(),
      bankTransferTxnId: '',
      paymentProofUrl,
      // Multi-payment method discount fields
      imPointsDiscount: imPointsDiscount || 0,
      imCashDiscount: imCashDiscount || 0,
      paymentMethods: paymentMethods || ['bank_transfer'],
      // Gross total before discounts — used as fallback when backend cart summarization returns 0
      grossOrderTotal: grossOrderTotal || 0,
    };

    CartApiService.bankTransferPlaceOrder(payload)
      .then((res) => {
        setPlacing(false);
        if (res?.status?.code === 200) {
          message.showToast('Order placed! We will confirm once payment is verified.', ToastTypes.SUCCESS);
          onOrderPlaced(res.data);
        } else {
          message.showToast(res?.error?.message || 'Failed to place order. Try again.', ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        setPlacing(false);
        message.showToast(err?.message || 'Server error. Try again.', ToastTypes.ERROR);
      });
  };

  if (!isOpen) return null;

  return (
    <div className="bt-modal-overlay" onClick={onClose}>
      <div className="bt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bt-modal-header">
          <div className="bt-modal-title-row">
            <MdAccountBalance size={22} className="bt-modal-title-icon" />
            <h3 className="bt-modal-title">Bank Transfer Payment</h3>
          </div>
          <button className="bt-modal-close" onClick={onClose} aria-label="Close">
            <MdClose size={20} />
          </button>
        </div>

        <div className="bt-modal-body">
          {loadingSettings ? (
            <div className="bt-loading">
              <div className="bt-spinner" />
              <p>Loading payment details…</p>
            </div>
          ) : !settings || (!settings.accountNumber && !settings.upi && !settings.qrImageUrl) ? (
            <div className="bt-no-settings">
              <p>Bank transfer details are not configured yet. Please contact support.</p>
            </div>
          ) : (
            <>
              <div className="bt-modal-top-grid">
                {/* QR Code */}
                <div className="bt-left-column">
                  {settings.qrImageUrl && (
                    <div className="bt-qr-section">
                      <div className="bt-qr-label">
                        <MdQrCode size={18} />
                        <span>Scan QR to Pay</span>
                      </div>
                      <div className="bt-qr-wrapper">
                        <img src={settings.qrImageUrl} alt="Payment QR Code" className="bt-qr-image" />
                      </div>
                    </div>
                  )}

                  <div className="bt-qr-note">
                    <strong>Note:</strong> Your order will be confirmed once our team verifies the payment.
                  </div>
                </div>

                {/* Bank Details */}
                <div className="bt-details-section">
                  <div className="bt-details-heading-row">
                    <h4 className="bt-details-heading">Bank Account Details</h4>
                    <div className="bt-total-pill">Pay: {formatAmount(totalPayable)}</div>
                  </div>
                  <div className="bt-details-grid">
                    {settings.accountHolderName && (
                      <BankDetailRow label="Account Holder" value={settings.accountHolderName} field="holder" copiedField={copiedField} onCopy={handleCopy} />
                    )}
                    {settings.bankName && (
                      <BankDetailRow label="Bank Name" value={settings.bankName} field="bank" copiedField={copiedField} onCopy={handleCopy} />
                    )}
                    {settings.accountNumber && (
                      <BankDetailRow label="Account Number" value={settings.accountNumber} field="account" copiedField={copiedField} onCopy={handleCopy} />
                    )}
                    {settings.ifsc && (
                      <BankDetailRow label="IFSC Code" value={settings.ifsc} field="ifsc" copiedField={copiedField} onCopy={handleCopy} />
                    )}
                    {settings.upi && (
                      <BankDetailRow label="UPI ID" value={settings.upi} field="upi" copiedField={copiedField} onCopy={handleCopy} />
                    )}
                    {settings.showGstToUser && settings.gstNumber && (
                      <BankDetailRow label="GST Number" value={settings.gstNumber} field="gst" copiedField={copiedField} onCopy={handleCopy} />
                    )}
                  </div>
                  <div className="bt-mini-proof-grid">
                    {/* Old: Transaction / UTR Reference ID (optional with OR upload)
                    <div className="bt-input-section">
                      <label className="bt-input-label">Transaction / UTR Reference ID</label>
                      <input
                        type="text"
                        className="bt-txn-input"
                        placeholder="Enter transaction / UTR ID after payment"
                        value={txnId}
                        onChange={(e) => setTxnId(e.target.value)}
                        disabled={placing}
                      />
                    </div>

                    <div className="bt-or-separator">
                      <span>OR</span>
                    </div>
                    */}

                    <div className="bt-upload-section">
                      <label className="bt-input-label">Upload Payment Proof *</label>
                      <div
                        className="bt-upload-area bt-upload-area-compact"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {proofPreview ? (
                          <img src={proofPreview} alt="Payment proof" className="bt-proof-preview" />
                        ) : proofFile ? (
                          <div className="bt-proof-filename">
                            <FiUpload size={16} />
                            <span>{proofFile.name}</span>
                          </div>
                        ) : (
                          <div className="bt-upload-placeholder">
                            <FiUpload size={20} />
                            <span>Click to upload screenshot or PDF (max 5MB)</span>
                          </div>
                        )}
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/jpg,application/pdf"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                        disabled={placing}
                      />
                      {proofFile && (
                        <button className="bt-remove-file" onClick={() => { setProofFile(null); setProofPreview(null); }}>
                          Remove file
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <button
                className="bt-place-order-btn"
                onClick={handlePlaceOrder}
                // disabled={placing || uploadingProof || (!txnId.trim() && !proofFile)}
                disabled={placing || uploadingProof || !proofFile}
              >
                {placing || uploadingProof ? (
                  <span className="bt-btn-loading">
                    <span className="bt-btn-spinner" />
                    {uploadingProof ? 'Uploading proof…' : 'Placing order…'}
                  </span>
                ) : (
                  'Place Order'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function formatAmount(value) {
  const amount = Number(value) || 0;
  return `₹${amount.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function BankDetailRow({ label, value, field, copiedField, onCopy }) {
  return (
    <div className="bt-detail-row">
      <span className="bt-detail-label">{label}</span>
      <div className="bt-detail-value-row">
        <span className="bt-detail-value">{value}</span>
        <button
          className="bt-copy-btn"
          onClick={() => onCopy(value, field)}
          title={`Copy ${label}`}
          type="button"
        >
          {copiedField === field ? <MdCheck size={15} color="#22c55e" /> : <MdContentCopy size={15} />}
        </button>
      </div>
    </div>
  );
}
